// Warning: This file (and any imports) are included in the main bundle with Grafana in order to provide link extension support in Grafana core, in an effort to keep Grafana loading quickly, please do not add any unnecessary imports to this file and run the bundle analyzer before committing any changes!
import { map as lodashMap } from 'lodash';

import {
  CustomVariableModel,
  PluginExtensionAddedLinkConfig,
  PluginExtensionPanelContext,
  PluginExtensionPoints,
  QueryVariableModel,
} from '@grafana/data';
import { getTemplateSrv, locationService } from '@grafana/runtime';

import pluginJson from '../../plugin.json';
import { LabelType } from '../fieldsTypes';
import { FieldFilter, IndexedLabelFilter, LineFilterType, PatternFilterOp, PatternFilterType } from '../filterTypes';
import { getLabelFormatIdentifiersFromQuery, getMatcherFromQuery } from '../logqlMatchers';
import { LokiQuery } from '../lokiQuery';
import { isOperatorInclusive } from '../operatorHelpers';
import { renderPatternFilters } from '../renderPatternFilters';
import { ensureValidTimeRangeForLink } from '../text';
import { escapeLabelValueInExactSelector, lokiSpecialRegexEscape } from './scenesMethods';
import {
  addAdHocFilterUserInputPrefix,
  AdHocFieldValue,
  AppliedPattern,
  EMPTY_VARIABLE_VALUE,
  LEVEL_VARIABLE_VALUE,
  SERVICE_NAME,
  stripAdHocFilterUserInputPrefix,
  VAR_DATASOURCE,
  VAR_FIELDS,
  VAR_LABELS,
  VAR_LEVELS,
  VAR_LINE_FILTERS,
  VAR_METADATA,
  VAR_PATTERNS,
} from 'services/variables';

const PRODUCT_NAME = 'Grafana Logs Drilldown';
const title = `Open in ${PRODUCT_NAME}`;
const description = `Open current query in the ${PRODUCT_NAME} view`;
const icon = 'gf-logs';

export type LinkConfigs = Array<PluginExtensionAddedLinkConfig<PluginExtensionPanelContext>>;

export const linkConfigs: LinkConfigs = [
  {
    targets: [
      PluginExtensionPoints.DashboardPanelMenu,
      PluginExtensionPoints.ExploreToolbarAction,
      'grafana-metricsdrilldown-app/open-in-logs-drilldown/v1',
      'grafana-assistant-app/navigateToDrilldown/v1',
    ],
    title,
    description,
    icon,
    path: createAppUrl(),
    configure: contextToLink,
  },
];

export const functionConfigs = [
  {
    targets: 'grafana-exploretraces-app/get-logs-drilldown-link/v1',
    title: 'Open Logs Drilldown',
    description: 'Returns url to logs drilldown app',
    fn: contextToLink,
  },
];

function stringifyValues(value?: string): string {
  if (!value) {
    return EMPTY_VARIABLE_VALUE;
  }
  return value;
}

// Why are there twice as many escape chars in the url as expected?
export function replaceEscapeChars(value?: string): string | undefined {
  return value?.replace(/\\\\/g, '\\');
}

export function stringifyAdHocValues(value?: string): string {
  if (!value) {
    return EMPTY_VARIABLE_VALUE;
  }

  // All label values from explore are already escaped, so we mark them as custom values to prevent them from getting escaped again when rendering the LogQL
  return addAdHocFilterUserInputPrefix(replaceEscapeChars(value));
}

export function stringifyAdHocValueLabels(value?: string): string {
  if (!value) {
    return EMPTY_VARIABLE_VALUE;
  }

  return escapeURLDelimiters(replaceEscapeChars(value));
}

function setUrlParamsFromFieldFilters(fields: FieldFilter[], params: URLSearchParams) {
  for (const field of fields) {
    if (field.type === LabelType.StructuredMetadata) {
      if (field.key === LEVEL_VARIABLE_VALUE) {
        params = appendUrlParameter(
          UrlParameters.Levels,
          `${field.key}|${field.operator}|${escapeURLDelimiters(stringifyValues(field.value))}`,
          params
        );
      } else {
        params = appendUrlParameter(
          UrlParameters.Metadata,
          `${field.key}|${field.operator}|${escapeURLDelimiters(
            stringifyAdHocValues(field.value)
          )},${escapeURLDelimiters(replaceEscapeChars(field.value))}`,
          params
        );
      }
    } else {
      const fieldValue: AdHocFieldValue = {
        value: field.value,
        parser: field.parser,
      };

      const adHocFilterURLString = `${field.key}|${field.operator}|${escapeURLDelimiters(
        stringifyAdHocValues(JSON.stringify(fieldValue))
      )},${stringifyAdHocValueLabels(fieldValue.value)}`;

      params = appendUrlParameter(UrlParameters.Fields, adHocFilterURLString, params);
    }
  }
  return params;
}

function setUrlParamsFromLabelFilters(labelFilters: IndexedLabelFilter[], params: URLSearchParams) {
  for (const labelFilter of labelFilters) {
    // skip non-indexed filters for now
    if (labelFilter.type !== LabelType.Indexed) {
      continue;
    }

    const labelsAdHocFilterURLString = `${labelFilter.key}|${labelFilter.operator}|${escapeURLDelimiters(
      stringifyAdHocValues(labelFilter.value)
    )},${escapeURLDelimiters(replaceEscapeChars(labelFilter.value))}`;

    params = appendUrlParameter(UrlParameters.Labels, labelsAdHocFilterURLString, params);
  }
  return params;
}

function setLineFilterUrlParams(lineFilters: LineFilterType[], params: URLSearchParams) {
  for (const lineFilter of lineFilters) {
    params = appendUrlParameter(
      UrlParameters.LineFilters,
      `${lineFilter.key}|${escapeURLDelimiters(lineFilter.operator)}|${escapeURLDelimiters(
        stringifyValues(lineFilter.value)
      )}`,
      params
    );
  }
  return params;
}

export function setUrlParamsFromPatterns(patternFilters: PatternFilterType[], params: URLSearchParams) {
  const patterns: AppliedPattern[] = [];

  for (const field of patternFilters) {
    patterns.push({
      type: field.operator === PatternFilterOp.match ? 'include' : 'exclude',
      pattern: stringifyValues(field.value),
    });
  }

  let patternsString = renderPatternFilters(patterns);

  params = appendUrlParameter(UrlParameters.Patterns, JSON.stringify(patterns), params);
  return appendUrlParameter(UrlParameters.PatternsVariable, patternsString, params);
}

const TRACES_TO_LOGS_FIELDS = ['log_line_contains_trace_id', 'log_line_contains_span_id'];
export function contextToLink<T extends PluginExtensionPanelContext>(context?: T) {
  if (!context || !context.targets) {
    return undefined;
  }
  const lokiQuery = context.targets.find((target) => target.datasource?.type === 'loki') as LokiQuery | undefined;
  const templateSrv = getTemplateSrv();
  const dataSourceUid = templateSrv.replace(lokiQuery?.datasource?.uid, context.scopedVars);

  if (!lokiQuery || !dataSourceUid) {
    return undefined;
  }

  // if there is no loki expression but the datasource is loki, then return createAppUrl()
  if (!lokiQuery?.expr) {
    return { path: createAppUrl() };
  }

  const expr = templateSrv.replace(lokiQuery.expr, context.scopedVars, interpolateQueryExpr);
  const { fields, labelFilters, lineFilters, patternFilters } = getMatcherFromQuery(expr, context, lokiQuery);
  const labelSelector = labelFilters.find((selector) => isOperatorInclusive(selector.operator));

  // If there's no label selector, return a link to the service selection
  // @todo it would be better if we could change the button copy (or tooltip) depending on the link destination
  if (!labelSelector) {
    return {
      path: createAppUrl(),
    };
  }

  // If there are a bunch of values for the same field, the value slug can get really long, let's just use the first one in the URL
  const urlLabelValue = labelSelector.value.split('|')[0];
  const labelValue = escapePrimaryLabel(urlLabelValue);
  let labelName = labelSelector.key === SERVICE_NAME ? 'service' : labelSelector.key;
  // sort `primary label` first
  labelFilters.sort((a) => (a.key === labelName ? -1 : 1));

  const fromMs = context.timeRange.from.valueOf();
  const toMs = context.timeRange.to.valueOf();
  const [from, to] = ensureValidTimeRangeForLink(Number(fromMs), Number(toMs));
  let params = setUrlParameter(UrlParameters.DatasourceId, dataSourceUid, new URLSearchParams());
  params = setUrlParameter(UrlParameters.TimeRangeFrom, from.toString(), params);
  params = setUrlParameter(UrlParameters.TimeRangeTo, to.toString(), params);
  params = setUrlParamsFromLabelFilters(labelFilters, params);

  if (lineFilters) {
    params = setLineFilterUrlParams(lineFilters, params);
  }
  if (fields?.length) {
    /**
     * When label_format labels are used as label filters, in Logs Drilldown they end up as invalid field filters
     * which produces no results. For example, log_line_contains_trace_id and log_line_contains_span_id
     * https://github.com/grafana/grafana/blob/43b5c5696acdfae5bee7f192c487cc5f5327065c/public/app/features/explore/TraceView/createSpanLink.tsx#L447-L455
     * Since these can't be used, we remove them from the filters.
     */
    const labelFormatFields = getLabelFormatIdentifiersFromQuery(expr);
    const filteredFields = fields.filter((field) => !labelFormatFields.includes(field.key));

    /**
     * With traces to logs fields present, we want to add these label filters after the parser, in case these are not
     * structured metadata but parsed.
     */
    const tracesToLogsFieldsPresent = fields.some((field) => TRACES_TO_LOGS_FIELDS.includes(field.key));
    if (tracesToLogsFieldsPresent) {
      filteredFields.forEach((field) => {
        field.parser = 'mixed';
        field.type = LabelType.Parsed;
      });
    }

    params = setUrlParamsFromFieldFilters(filteredFields, params);
  }
  if (patternFilters?.length) {
    params = setUrlParamsFromPatterns(patternFilters, params);
  }
  // @ts-expect-error Requires Grafana 12.4
  if (context.sortOrder) {
    // @ts-expect-error Requires Grafana 12.4
    params = appendUrlParameter(UrlParameters.SortOrder, JSON.stringify(context.sortOrder), params);
  }

  return {
    path: createAppUrl(`/explore/${labelName}/${labelValue}/logs`, params),
  };
}

export function createAppUrl(path = '/explore', urlParams?: URLSearchParams): string {
  return `/a/${pluginJson.id}${path}${urlParams ? `?${urlParams.toString()}` : ''}`;
}

export const UrlParameters = {
  DatasourceId: `var-${VAR_DATASOURCE}`,
  TimeRangeFrom: 'from',
  TimeRangeTo: 'to',
  Labels: `var-${VAR_LABELS}`,
  Fields: `var-${VAR_FIELDS}`,
  Metadata: `var-${VAR_METADATA}`,
  Levels: `var-${VAR_LEVELS}`,
  LineFilters: `var-${VAR_LINE_FILTERS}`,
  Patterns: VAR_PATTERNS,
  PatternsVariable: `var-${VAR_PATTERNS}`,
  SortOrder: 'sortOrder',
} as const;
export type UrlParameterType = (typeof UrlParameters)[keyof typeof UrlParameters];

export function setUrlParameter(key: UrlParameterType, value: string, initalParams?: URLSearchParams): URLSearchParams {
  const searchParams = new URLSearchParams(initalParams?.toString() ?? locationService.getSearch());
  searchParams.set(key, value);

  return searchParams;
}

export function appendUrlParameter(
  key: UrlParameterType,
  value: string,
  initalParams?: URLSearchParams
): URLSearchParams {
  const location = locationService.getLocation();
  const searchParams = new URLSearchParams(initalParams?.toString() ?? location.search);
  searchParams.append(key, value);

  return searchParams;
}

export function escapePrimaryLabel(parameter: string): string {
  const normalized = stripAdHocFilterUserInputPrefix(parameter)
    // back-slash is converted to forward-slash in the URL, replace that char
    .replace(/\//g, '-')
    .replace(/\\/g, '-');
  // Encode so regex/special chars in path (e.g. ()) don't break routing
  return encodeURIComponent(normalized);
}

export function restoreLabelValueFromUrlParam(value: string): string {
  return decodeURIComponent(value);
}

// Manually copied over from @grafana/scenes so we don't need to import scenes to build links
function escapeUrlCommaDelimiters(value: string | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Replace the comma due to using it as a value/label separator
  return /,/g[Symbol.replace](value, '__gfc__');
}

export function escapeUrlPipeDelimiters(value: string | undefined): string {
  if (value === null || value === undefined) {
    return '';
  }

  // Replace the pipe due to using it as a filter separator
  return (value = /\|/g[Symbol.replace](value, '__gfp__'));
}

export function escapeURLDelimiters(value: string | undefined): string {
  return escapeUrlCommaDelimiters(escapeUrlPipeDelimiters(value));
}

// Copied from interpolateQueryExpr in loki datasource, as we can't return a promise in the link extension config we can't fetch the datasource from the datasource srv, so we're forced to duplicate this method
export function interpolateQueryExpr(value: string | unknown[], variable: QueryVariableModel | CustomVariableModel) {
  // if no multi or include all do not regexEscape
  if (!variable.multi && !variable.includeAll) {
    return value;
  }

  if (typeof value === 'string') {
    return escapeLabelValueInExactSelector(value);
  }

  const escapedValues = lodashMap(value, lokiSpecialRegexEscape);
  return escapedValues.join('|');
}
